import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useWatch } from 'react-hook-form';
import { TextField } from '@material-ui/core';
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ClientTextFieldController from '../../../Common/ClientTextFieldController';
import SearchableStatesDropdown from '../../../Common/SearchableStatesDropdown';
import { RootState } from '../../../../../../redux/root';
import Dropdown from '../../../Common/Controls/Dropdown';
import { InputAutocompletePlaces } from '../../../../../../molecules';
import UploadFiles from '../../../Common/UploadFiles';
import { setSelectedFranchise } from '../../../../../../redux/slices/projectSlice';
import ServiceType from './ServiceType';
import useGetFranchiseUsers from '../../../../hooks/useGetFranchiseUsers';
import GeneralTextField from '../../../Common/Controls/GeneralTextField';
import FranchiseContacts from './FranchiseContacts';
import ApplicantTypeCards from './ApplicantTypeCards';
import OfficeCards from './OfficeCards';

type Props = {
  control: any;
  register: any;
  trigger: Function;
  errors: any;
  setValue: Function;
  getValues: Function;
  clearErrors: Function;
  reset: Function;
  currentStep: number;
};
const ProjectControlsArea: FC<Props> = ({
  control,
  register,
  errors,
  setValue,
  trigger,
  clearErrors,
  currentStep,
}) => {
  const { loadFranchiseUsers } = useGetFranchiseUsers();
  const vocabularies = useSelector((state: RootState) => state.general.vocabulary);
  const [selectedFranchiseUsers, setSelectedFranchiseUsers] = useState([]);
  const { franchises, franchiseUsers, selectedFranchise } = useSelector((s: RootState) => s.projectsSlice);

  useEffect(() => {
    if (selectedFranchise.id) {
      loadFranchiseUsers(selectedFranchise.id.toString());
    }
  }, [selectedFranchise]);

  useEffect(() => {
    const franchaseUsersList = franchiseUsers.find(u => Number(u.id) === selectedFranchise.id);
    if (selectedFranchise.id && franchaseUsersList) {
      setSelectedFranchiseUsers(franchaseUsersList.value);
    }
  }, [selectedFranchise, franchiseUsers]);

  const dispatch = useDispatch();
  const [addressVerified, setAddressVerified] = useState<boolean>(false);
  const [verifiedAddress, setVerifiedAddress] = useState<string>('');
  const [manualOpen, setManualOpen] = useState<boolean>(false);
  const [
    projectLatLng,
    setProjectLatLng,
  ] = useState<{ lat: number; lng: number } | null>(null);

  const handlePlaceSelect = (address, isSelect) => {
    if (address || isSelect) {
      clearErrors('address');
    }
    if (isSelect) {
      Object.entries(address).map(([key, value]) => {
        setValue(key, value);
        clearErrors(key);
        return null;
      });
      setVerifiedAddress(address.address || '');
      setAddressVerified(true);
      setManualOpen(false);
      return null;
    }
    if (addressVerified) {
      setAddressVerified(false);
    }
    return setValue('address', address);
  };

  const onClearAddress = () => {
    setAddressVerified(false);
    setVerifiedAddress('');
    setManualOpen(true);
    setProjectLatLng(null);
    setValue('address', '');
    setValue('street', '');
    setValue('city', '');
    setValue('state', '');
    setValue('zipCode', '');
  };

  const projectState = useWatch({ control, name: 'state' }) as string | undefined;
  const manualStreet = useWatch({ control, name: 'street' }) as string | undefined;
  const manualCity = useWatch({ control, name: 'city' }) as string | undefined;
  const manualZip = useWatch({ control, name: 'zipCode' }) as string | undefined;

  useEffect(() => {
    if (addressVerified) return;
    const composed = [manualStreet, manualCity, projectState, manualZip]
      .filter(Boolean)
      .join(', ');
    setValue('address', composed);
    if (composed) {
      clearErrors('address');
    }
  }, [manualStreet, manualCity, projectState, manualZip, addressVerified]);

  const [contactPickerOpen, setContactPickerOpen] = useState<boolean>(false);

  useEffect(() => {
    register('franchise');
    register('manager');
  }, [register]);

  const onPickFranchise = (fr: { id: number; name: string }) => {
    const item = { id: fr.id, title: fr.name };
    setValue('franchise', [item]);
    clearErrors('franchise');
    dispatch(setSelectedFranchise(item));
    trigger('franchise');
  };

  useEffect(() => {
    if (selectedFranchise.id) {
      setValue('manager', '');
      setContactPickerOpen(false);
    }
  }, [selectedFranchise.id]);

  return (
    <ProjectFields>
      <StepPane $active={currentStep === 1}>
        <Section>
          <SectionHead>
            <SectionTitle>Project Location</SectionTitle>
            <SectionHint>Where will the work take place?</SectionHint>
          </SectionHead>
          <SectionBody>
            <HeroLabel>Find your address</HeroLabel>
            <HeroAutocompleteWrapper $error={errors.address !== undefined}>
              <PinIcon>
                <RoomOutlinedIcon style={{ fontSize: 22 }} />
              </PinIcon>
              <Controller
                as={InputAutocompletePlaces}
                control={control}
                id="filled-error-helper-text"
                name="address"
                label=""
                defaultValue=""
                style={{ width: '100%' }}
                helperText=""
                inputRef={register}
                error={errors.address !== undefined}
                handleFieldChange={handlePlaceSelect}
                onLatLng={setProjectLatLng}
              />
            </HeroAutocompleteWrapper>
            <HeroHint>
              Start typing — we&apos;ll fill in the rest.{' '}
              {!addressVerified && !manualOpen && (
                <ManualLink type="button" onClick={() => setManualOpen(true)}>
                  Don&apos;t see it? Enter manually
                </ManualLink>
              )}
            </HeroHint>

            {addressVerified && (
              <VerifiedCard>
                <VerifiedHead>
                  <VerifiedIcon>
                    <CheckCircleIcon style={{ fontSize: 20 }} />
                  </VerifiedIcon>
                  <VerifiedText>
                    <VerifiedTitle>Address verified</VerifiedTitle>
                    <VerifiedAddressText>{verifiedAddress}</VerifiedAddressText>
                  </VerifiedText>
                  <VerifiedActions>
                    <ToggleManualBtn
                      type="button"
                      onClick={() => setManualOpen(o => !o)}
                    >
                      <EditOutlinedIcon style={{ fontSize: 14 }} />
                      {manualOpen ? 'Hide details' : 'Edit details'}
                    </ToggleManualBtn>
                    <ClearBtn type="button" onClick={onClearAddress}>
                      Change
                    </ClearBtn>
                  </VerifiedActions>
                </VerifiedHead>
              </VerifiedCard>
            )}

            <ManualGroup
              style={{
                display:
                  manualOpen ||
                  errors.street ||
                  errors.city ||
                  errors.state ||
                  errors.zipCode
                    ? 'flex'
                    : 'none',
              }}
            >
              <ManualEyebrow>Address details</ManualEyebrow>
              <StreetRow>
                <GeneralTextField name="street" label="Street" errors={errors} register={register} required placeholder="123 Main St" />
              </StreetRow>
              <CityStateZipRow>
                <GridArea area="city">
                  <GeneralTextField name="city" label="City" errors={errors} register={register} required placeholder="Tampa" />
                </GridArea>
                <GridArea area="state">
                  <SearchableStatesDropdown register={register} errors={errors} setValue={setValue} control={control} required />
                </GridArea>
                <GridArea area="zip">
                  <GeneralTextField name="zipCode" label="Zip" errors={errors} register={register} required placeholder="33601" />
                </GridArea>
              </CityStateZipRow>
            </ManualGroup>
          </SectionBody>
        </Section>
      </StepPane>

      <StepPane $active={currentStep === 2}>
        <Section>
          <SectionHead>
            <SectionTitle>Project Details</SectionTitle>
            <SectionHint>Give your project a memorable name and describe the scope. Staff may refine the name when processing.</SectionHint>
          </SectionHead>
          <SectionBody>
            <FieldRow>
              <ClientTextFieldController name="projectName" label="Project Name" errors={errors} control={control} register={register} required />
            </FieldRow>
            <FieldRow>
              <Controller
                as={TextField}
                control={control}
                id="filled-error-helper-text"
                name="description"
                variant="outlined"
                label="Description"
                defaultValue=""
                style={{ width: '100%' }}
                helperText={errors.description ? errors.description.message : ''}
                inputRef={register}
                error={errors.description !== undefined}
                InputLabelProps={{ required: true }}
                multiline
                rows={4}
                rowsMax={8}
                placeholder="Describe the scope of work in as much detail as you can."
              />
            </FieldRow>
          </SectionBody>
        </Section>
      </StepPane>

      <StepPane $active={currentStep === 3}>
        <Section>
          <SectionHead>
            <SectionTitle>Services Requested</SectionTitle>
            <SectionHint>Toggle the services you need from Elite Permits. Hover a toggle for a short description.</SectionHint>
          </SectionHead>
          <SectionBody>
            <ServiceType errors={errors} setValue={setValue} trigger={trigger} register={register} />
          </SectionBody>
        </Section>
      </StepPane>

      <StepPane $active={currentStep === 4}>
        <Section>
          <SectionHead>
            <SectionTitle>Applicant &amp; Scope</SectionTitle>
            <SectionHint>
              Two quick questions about who you are and what the job involves.
            </SectionHint>
          </SectionHead>
          <SectionBody>
            <SubGroup>
              <SubEyebrow>Who&apos;s applying?</SubEyebrow>
              <ApplicantTypeCards
                errors={errors}
                setValue={setValue}
                trigger={trigger}
                register={register}
              />
            </SubGroup>

            <SubGroup>
              <SubEyebrow>About the job</SubEyebrow>
              <SubHint>Pick the closest match — we can refine it later.</SubHint>
              <FieldGrid>
                <Dropdown
                  register={register}
                  errors={errors}
                  control={control}
                  dropdownItems={vocabularies.filter(item => item.vocabularyEntity === 2)}
                  name="work"
                  label="Kind of work"
                  required
                />
                <Dropdown
                  register={register}
                  errors={errors}
                  control={control}
                  dropdownItems={vocabularies.filter(item => item.vocabularyEntity === 1)}
                  name="building"
                  label="Type of building"
                  required
                />
              </FieldGrid>
            </SubGroup>
          </SectionBody>
        </Section>
      </StepPane>

      <StepPane $active={currentStep === 5}>
        <Section>
          <SectionHead>
            <SectionTitle>Office</SectionTitle>
            <SectionHint>
              Pick the office you&apos;d like to work with. We&apos;ll route your request to them.
            </SectionHint>
          </SectionHead>
          <SectionBody>
            <SubGroup>
              <SubEyebrow>Choose your office</SubEyebrow>
              <OfficeCards
                franchises={franchises}
                selectedId={selectedFranchise.id || null}
                onSelect={onPickFranchise}
                projectState={projectState}
                projectLatLng={projectLatLng}
                errors={errors}
              />
              <ContactToggleRow>
                {!contactPickerOpen ? (
                  <InlineLink
                    type="button"
                    disabled={!selectedFranchise.id}
                    onClick={() => setContactPickerOpen(true)}
                  >
                    + Already working with a contact here?
                  </InlineLink>
                ) : (
                  <ManagerWrapper>
                    <ManagerLabel>Office contact (optional)</ManagerLabel>
                    <ManagerHint>
                      We&apos;ll route your request straight to them.
                    </ManagerHint>
                    <FranchiseContacts
                      register={register}
                      name="manager"
                      dropdownItems={selectedFranchiseUsers.map((m: any) => ({
                        title: `${m.firstName} ${m.lastName}`,
                        id: m.id,
                      }))}
                      setValue={setValue}
                      trigger={trigger}
                    />
                    <InlineLink
                      type="button"
                      onClick={() => {
                        setContactPickerOpen(false);
                        setValue('manager', '');
                      }}
                    >
                      Skip — no contact
                    </InlineLink>
                  </ManagerWrapper>
                )}
              </ContactToggleRow>
            </SubGroup>
          </SectionBody>
        </Section>
      </StepPane>

      <StepPane $active={currentStep === 6}>
        <Section>
          <SectionHead>
            <SectionTitle>Anything to share?</SectionTitle>
            <SectionHint>
              Attach plans, drawings, or paperwork so the office has everything they need to start.
            </SectionHint>
          </SectionHead>
          <SectionBody>
            <SubGroup>
              <SubEyebrow>Supporting documents</SubEyebrow>
              <SubHint>
                PDF, up to 5&nbsp;MB total. Optional &mdash; you can add files later from your dashboard.
              </SubHint>
              <UploadFiles setValue={setValue} register={register} />
              <ExampleStrip>
                <ExampleLabel>Examples</ExampleLabel>
                <ExampleChip>Site plans</ExampleChip>
                <ExampleChip>Existing drawings</ExampleChip>
                <ExampleChip>Permits</ExampleChip>
                <ExampleChip>Contractor proposal</ExampleChip>
              </ExampleStrip>
            </SubGroup>
          </SectionBody>
        </Section>
      </StepPane>
    </ProjectFields>
  );
};

export default ProjectControlsArea;

const ProjectFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
`;

const StepPane = styled.div<{ $active: boolean }>`
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  box-sizing: border-box;

  @media only screen and (max-width: 540px) {
    padding: 16px;
  }
`;

const SectionHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding-bottom: 12px;
  border-bottom: 1px dashed var(--border);
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;
`;

const SectionHint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--neutral-500);
  line-height: 1.45;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const SubGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const SubEyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const SubHint = styled.p`
  margin: 0 0 4px;
  font-size: 0.8125rem;
  color: var(--neutral-500);
  line-height: 1.45;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 20px;
  align-items: start;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 18px 0;
  }
`;

export const RequiredWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
`;

export const Required = styled.div<{ left?: number; top?: number }>`
  display: none;
`;

export const DoubleControllsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 24px;
  @media only screen and (max-width: 840px) {
    grid-template-columns: 1fr;
    gap: 20px 0;
  }
`;

export const TextLabel = styled.div<{ isError: boolean }>`
  color: ${({ isError }) =>
    isError ? 'var(--danger-500, #dc2626)' : 'var(--neutral-600)'};
  font-size: 0.875rem;
  font-weight: 600;
`;

const ManagerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const ManagerLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const ManagerHint = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
  line-height: 1.4;
`;

const ContactToggleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const InlineLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 4px 0;
  margin: 0;
  font: inherit;
  font-size: 0.8125rem;
  color: var(--primary-600);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: var(--primary-700, #0b5a87);
    text-decoration: underline;
  }

  &:disabled {
    color: var(--neutral-400);
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: 4px;
  }
`;

const ExampleStrip = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const ExampleLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-right: 4px;
`;

const ExampleChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  background: var(--neutral-50);
  color: var(--neutral-600);
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
`;

const HeroLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const HeroAutocompleteWrapper = styled.div<{ $error: boolean }>`
  position: relative;
  width: 100%;

  && .MuiInputBase-root {
    height: 48px !important;
    border-radius: 10px !important;
  }

  && .MuiOutlinedInput-input {
    padding: 0 14px 0 44px !important;
    height: 48px !important;
    font-size: 0.9375rem !important;
    box-sizing: border-box;
  }

  && .MuiOutlinedInput-notchedOutline {
    border-color: ${({ $error }) =>
      ($error ? 'var(--danger-500, #dc2626)' : 'var(--border)')} !important;
    border-radius: 10px !important;
  }

  && .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: var(--neutral-400) !important;
  }

  && .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: var(--primary-600) !important;
    box-shadow: var(--shadow-focus);
  }
`;

const PinIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  color: var(--primary-600);
  pointer-events: none;
  z-index: 1;
`;

const HeroHint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--neutral-500);
  line-height: 1.45;
`;

const ManualLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: var(--primary-600);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: var(--primary-700, #0b5a87);
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: 4px;
  }
`;

const VerifiedCard = styled.div`
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 10px;
  padding: 14px 16px;
`;

const VerifiedHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  @media only screen and (max-width: 540px) {
    flex-wrap: wrap;
  }
`;

const VerifiedIcon = styled.span`
  display: inline-flex;
  color: #059669;
  flex-shrink: 0;
  margin-top: 2px;
`;

const VerifiedText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const VerifiedTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #047857;
`;

const VerifiedAddressText = styled.span`
  font-size: 0.875rem;
  color: var(--mainText);
  word-break: break-word;
  font-weight: 500;
`;

const VerifiedActions = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-shrink: 0;

  @media only screen and (max-width: 540px) {
    width: 100%;
    margin-top: 4px;
  }
`;

const verifiedBtn = `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ToggleManualBtn = styled.button`
  ${verifiedBtn}
  border: 1px solid #a7f3d0;
  background: var(--neutral-0);
  color: #047857;

  &:hover {
    background: #d1fae5;
  }
`;

const ClearBtn = styled.button`
  ${verifiedBtn}
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--neutral-700);

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
`;

const ManualGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const ManualEyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const StreetRow = styled.div`
  width: 100%;
`;

const CityStateZipRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 14px;
  align-items: start;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'city city'
      'state zip';
    gap: 14px;
  }

  @media only screen and (max-width: 420px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'city'
      'state'
      'zip';
  }
`;

const GridArea = styled.div<{ area: 'city' | 'state' | 'zip' }>`
  grid-area: ${({ area }) => area};
  min-width: 0;

  @media (min-width: 641px) {
    grid-area: auto;
  }
`;
